import { Box, CircularProgress, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRecommendationsContext } from "../../contexts/Recommendations/RecommendationsContext";
import { type Recommendation, Severity } from "../../utils/models";
import { NormalizedRequestApprovalDialog } from "../approvalDialogs/RequestsApproval/NormalizedRequestApprovalDialog";
import { StaffingChanges } from "../StaffingChanges/StaffingChanges";
import { RecommendationsHeader } from "./Header/RecommendationsHeader";
import { useRecommendations } from "./hooks/useRecommendations";
import { RecommendationItem } from "./RecommendationItem";

const DEFAULT_SELECTED_SEVERITIES = [Severity.High, Severity.Medium, Severity.Low] as const satisfies readonly Severity[];

type RecommendationsProps = { date?: DateTime };

export const Recommendations: FC<RecommendationsProps> = ({ date }) => {
	const { departmentNurseTypes } = useHospitalData();
	const { recommendations, isLoading, setRecommendations } = useRecommendationsContext();
	const {
		selectedDate,
		staffingChangesTab,
		selectedRequest,
		selectedRecommendation,
		onComplete: onRecommendationComplete,
		onClose,
		onApprove,
	} = useRecommendations();
	const [selectedSeverities, setSelectedSeverities] = useState<ReadonlySet<Severity>>(() => new Set(DEFAULT_SELECTED_SEVERITIES));
	const [completedRecommendationId, setCompletedRecommendationId] = useState<string | null>(null);

	const activeRecommendations = useMemo<Recommendation[]>(() => {
		if (date) return recommendations.filter((recommendation) => recommendation.date.hasSame(date, "day"));
		return recommendations;
	}, [recommendations, date]);

	const recommendationsBySeverity = useMemo<Map<Severity, Recommendation[]>>(() => {
		const groupedBySeverity = new Map<Severity, Recommendation[]>(Object.values(Severity).map((severity) => [severity, []]));

		activeRecommendations.forEach((recommendation) => {
			groupedBySeverity.get(recommendation.severity)?.push(recommendation);
		});

		return groupedBySeverity;
	}, [activeRecommendations]);

	const selectedRecommendations = useMemo<Recommendation[]>(
		() => activeRecommendations.filter((recommendation) => selectedSeverities.has(recommendation.severity)),
		[activeRecommendations, selectedSeverities],
	);

	const onSelect = useCallback((severity: Severity): void => {
		setSelectedSeverities((prevSeverities) => {
			const newSeverities = new Set(prevSeverities);
			newSeverities.has(severity) ? newSeverities.delete(severity) : newSeverities.add(severity);
			return newSeverities.size ? newSeverities : new Set(DEFAULT_SELECTED_SEVERITIES);
		});
	}, []);

	const onComplete = useCallback(
		async (idx?: number, success?: boolean): Promise<void> => {
			if (success && selectedRecommendation) {
				setCompletedRecommendationId(selectedRecommendation.id);
				await new Promise((resolve) => setTimeout(resolve, 1500));
				setRecommendations((prev) => prev.filter((rec) => rec.id !== selectedRecommendation.id));
				setCompletedRecommendationId(null);
			}
			await onRecommendationComplete(idx);
		},
		[onRecommendationComplete, selectedRecommendation, setRecommendations],
	);

	return (
		<>
			<Box>
				<Box
					sx={{
						position: "sticky",
						top: 0,
						zIndex: 1,
						backgroundColor: "white",
					}}
				>
					<RecommendationsHeader
						count={selectedRecommendations.length}
						selectedSeverities={selectedSeverities}
						onSelect={onSelect}
						onSelectAll={() => setSelectedSeverities(new Set(DEFAULT_SELECTED_SEVERITIES))}
						recommendationsBySeverity={recommendationsBySeverity}
					/>
				</Box>
				{!isLoading ? (
					<AnimatePresence mode="popLayout">
						{selectedRecommendations.map((recommendation: Recommendation) => (
							<motion.div
								key={recommendation.id}
								layout
								initial={{ opacity: 0, scale: 0.8, y: 20 }}
								animate={{ opacity: 1, scale: 1, y: 0 }}
								exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
								transition={{ type: "spring", stiffness: 300, damping: 30 }}
							>
								<RecommendationItem
									recommendation={recommendation}
									onApprove={onApprove}
									showSuccessAnimation={completedRecommendationId === recommendation.id}
								/>
							</motion.div>
						))}
					</AnimatePresence>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							minHeight: 200,
							gap: 2,
						}}
					>
						<CircularProgress />
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
							}}
						>
							Recommendations are loading...
						</Typography>
					</Box>
				)}
			</Box>
			{staffingChangesTab && selectedDate && (
				<StaffingChanges
					setIsAdd={() => onComplete(undefined, true)}
					dayNo={selectedDate.weekday % 7}
					shiftNo={0}
					rolesToDisplay={departmentNurseTypes}
					onClose={(success?: boolean) => onComplete(undefined, success)}
					defaultTab={staffingChangesTab}
					// Opened on a whole day, not one shift: Add Staff asks which shift first.
					initialShiftSelected={false}
				/>
			)}
			{selectedRequest && selectedDate && (
				<NormalizedRequestApprovalDialog isRecommendation={true} request={selectedRequest} onClose={onClose} open={!!selectedRequest} />
			)}
		</>
	);
};
